/**
 * The cluster → databases graph, as pure data.
 *
 * `clusterGraph(cluster)` answers "which managed databases is this cluster ALLOWED to
 * reach", and the whole point of the screen is the COLOUR: an edge is not "there is a
 * connection", it is "this permission is healthy" — every node of the cluster has a
 * live agent that is actually on the database's firewall / access list. So the health
 * vocabulary is the contract, not a styling detail, and it lives here rather than
 * inline in the component:
 *
 *   green   every agent live and present            → the permission works today
 *   red     an agent is silent, or is not on the list → someone will be paged
 *   amber   all agents good, but nodes run no agent   → unknown, never claimed good
 *   neutral no evidence at all (never read/scanned)   → we do not know, and say so
 *
 * `neutral` is the ONLY dashed style. That is deliberate and pinned by
 * `scripts/check-cluster-graph.mjs`: dashing means "this line is not a fact", and if a
 * second health borrowed it the one thing a person can read from across the room stops
 * being true.
 *
 * Pure and node-importable (no React, no @xyflow types) so the check script can call it
 * directly — same rule as `targetKind.ts` and `targetEngine.ts`. The health and evidence
 * VOCABULARY is not defined here: it comes from `@infras/shared-types` (contract §8), so
 * the resolver, the MCP tool and this screen cannot drift into disagreeing about which
 * colour means "nobody knows".
 */
import { EDGE_HEALTHS, EDGE_HEALTH_LABEL, isEdgeHealth, type EdgeHealth } from '@infras/shared-types';

export { EDGE_HEALTHS, type EdgeHealth };

export interface ClusterGraphNode {
  id: string;
  kind: string;
  label: string;
  sub?: string | null;
  engine?: string | null;
  team?: string | null;
  targetKey?: string | null;
}

export interface ClusterGraphEdge {
  id: string;
  source: string;
  target: string;
  verb?: string | null;
  confidence?: string | null;
  health?: string | null;
  reason?: string | null;
  agentsGood?: number | null;
  agentsTotal?: number | null;
  nodesTotal?: number | null;
  evidence?: string | null;
  listReadAt?: string | null;
}

export interface ClusterGraph {
  focus: ClusterGraphNode;
  nodes: ClusterGraphNode[];
  edges: ClusterGraphEdge[];
  doScanAt?: string | null;
}

/**
 * health → stroke + dash.
 *
 * The strokes are `var(--token, #literal)`: the tokens are the app's palette (a theme
 * change must move these lines too), and the literal fallback is there because these
 * strings are also handed to an inline SVG `stroke` in a canvas that renders before
 * any stylesheet on a cold load. Green is the literal only — `--green` and `#35c26b`
 * are the same value and `EDGE_STYLE` in `ServiceGraph.tsx` already spells it that way.
 */
export const HEALTH_STYLE: Record<EdgeHealth, { stroke: string; dash?: string }> = {
  green: { stroke: '#35c26b' },
  red: { stroke: 'var(--bad, #ff5c5c)' },
  amber: { stroke: 'var(--amber, #e0a83a)' },
  // The only dashed style. Grey, dashed, and never animated: nothing is happening,
  // we simply have not read the list.
  neutral: { stroke: '#8b93a3', dash: '6 4' },
};

/** What the Health cell READS — never colour alone (AC-13). One table, in shared-types:
 *  `neutral` reads "unknown" to a person, and that wording is the contract's, not ours. */
export const HEALTH_LABEL = EDGE_HEALTH_LABEL;

/** Order the rows and the stack: worst first, then alphabetical. A person opens this
 *  tab to find the broken permission, so it must not be below the fold. */
const HEALTH_RANK: Record<EdgeHealth, number> = { red: 0, amber: 1, green: 2, neutral: 3 };

/** Anything outside the four names degrades to `neutral` rather than throwing — the
 *  server owns the vocabulary and a future fifth name must not blank the screen. */
export function asHealth(v: string | null | undefined): EdgeHealth {
  return isEdgeHealth(v) ? v : 'neutral';
}

// ── layout ───────────────────────────────────────────────────────────────────
// One column of databases to the right of the focus. Same idiom as ServiceGraph's
// two columns; the numbers are smaller because this canvas is 320px, not the viewport.
export const FOCUS_X = 0;
export const DB_X = 380;
const FOCUS_W = 240;
const FOCUS_H = 64;
const DB_W = 260;
const DB_H = 54;
const DB_GAP = 12;

export interface FlowNode {
  id: string;
  type: string;
  data: Record<string, unknown>;
  position: { x: number; y: number };
  style: { width: number; height: number };
  connectable: boolean;
  selectable: boolean;
}

export interface FlowEdge {
  id: string;
  source: string;
  target: string;
  type: string;
  data: Record<string, unknown>;
}

/**
 * The React-Flow shape. Kept out of the component so the check script can assert the
 * placement and the ordering without a DOM: every edge runs focus → database (left to
 * right, never the reverse), and every edge's endpoints exist among the nodes.
 */
export function buildFlow(graph: ClusterGraph | null | undefined): {
  nodes: FlowNode[];
  edges: FlowEdge[];
} {
  if (!graph?.focus) return { nodes: [], edges: [] };
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const rows = rowsFor(graph);

  const stackH = Math.max(rows.length, 1) * (DB_H + DB_GAP) - DB_GAP;
  const nodes: FlowNode[] = [
    {
      id: graph.focus.id,
      type: 'clusterFocus',
      data: {
        label: graph.focus.label,
        sub: graph.focus.sub ?? null,
        health: 'neutral',
        nodeId: graph.focus.id,
      },
      // Vertically centred against the database stack, so the fan reads as a fan.
      position: { x: FOCUS_X, y: Math.max(0, (stackH - FOCUS_H) / 2) },
      style: { width: FOCUS_W, height: FOCUS_H },
      connectable: false,
      selectable: false,
    },
  ];

  const edges: FlowEdge[] = [];
  rows.forEach((r, i) => {
    const node = byId.get(r.id);
    nodes.push({
      id: r.id,
      type: 'clusterDb',
      data: {
        label: r.name,
        team: r.team,
        engine: node?.engine ?? null,
        kind: node?.kind ?? '',
        health: r.health,
        nodeId: r.id,
      },
      position: { x: DB_X, y: i * (DB_H + DB_GAP) },
      style: { width: DB_W, height: DB_H },
      connectable: false,
      selectable: false,
    });
    edges.push({
      id: r.edgeId,
      // Always focus → database. The verb is `connectsTo` and the axis of this canvas
      // is "what may this cluster reach"; a reversed edge would read as the opposite.
      source: graph.focus.id,
      target: r.id,
      type: 'health',
      data: { health: r.health, edgeId: r.edgeId, reason: r.reason },
    });
  });

  return { nodes, edges };
}

export interface DbRow {
  /** node id — `db:do:<uuid>` / `db:atlas:<projectKey>` */
  id: string;
  edgeId: string;
  name: string;
  engine: string | null;
  team: string | null;
  health: EdgeHealth;
  /** the word in the Health cell */
  badge: string;
  agentsGood: number | null;
  agentsTotal: number | null;
  nodesTotal: number | null;
  /** "2 / 5", or "—" when the server sent no counts */
  agents: string;
  reason: string;
  evidence: string;
  listReadAt: string | null;
  /** the drawer, or null — never a dead link */
  href: string | null;
}

/** One row per EDGE (the table is the list of permissions, not of databases): a node
 *  with no edge is not something this cluster may reach. */
export function rowsFor(graph: ClusterGraph | null | undefined): DbRow[] {
  if (!graph) return [];
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const focusId = graph.focus?.id;
  const rows: DbRow[] = [];
  for (const e of graph.edges) {
    // The database is whichever end is not the cluster, so an edge the server ever
    // emits target-first still lands on the right row.
    const dbId = e.target === focusId ? e.source : e.target;
    const node = byId.get(dbId);
    if (!node || dbId === focusId) continue;
    const health = asHealth(e.health);
    rows.push({
      id: dbId,
      edgeId: e.id,
      name: node.label,
      engine: node.engine ?? null,
      team: node.team ?? null,
      health,
      badge: HEALTH_LABEL[health],
      agentsGood: e.agentsGood ?? null,
      agentsTotal: e.agentsTotal ?? null,
      nodesTotal: e.nodesTotal ?? null,
      agents: e.agentsTotal == null ? '—' : `${e.agentsGood ?? 0} / ${e.agentsTotal}`,
      reason: e.reason ?? '—',
      evidence: e.evidence ?? '—',
      listReadAt: e.listReadAt ?? null,
      href: dbHref(node),
    });
  }
  return rows.sort(
    (a, b) => HEALTH_RANK[a.health] - HEALTH_RANK[b.health] || a.name.localeCompare(b.name),
  );
}

/**
 * The drawer for a database that is a REGISTERED access-list target, or null.
 *
 * A scanned DigitalOcean database that nobody has added as a target has no drawer to
 * open, so it gets no `<a>` at all (AC-13). A link to a 404 is worse than plain text:
 * it teaches people the row is broken rather than that the target is unregistered.
 */
export function dbHref(node: ClusterGraphNode | null | undefined): string | null {
  const key = node?.targetKey;
  if (!key) return null;
  return `/cluster-access/project/${encodeURIComponent(key)}`;
}

/** Counts per health, for the section head. */
export function healthSummary(
  edges: readonly ClusterGraphEdge[] | null | undefined,
): Record<EdgeHealth, number> {
  const out: Record<EdgeHealth, number> = { green: 0, red: 0, amber: 0, neutral: 0 };
  for (const e of edges ?? []) out[asHealth(e.health)] += 1;
  return out;
}

/**
 * "DigitalOcean has never been scanned on this environment" — true on prod today.
 * Without the scan the `k8s`/`ip_addr` rules simply are not in the database, so an
 * empty canvas would read as "this cluster reaches nothing", which is a lie. The tab
 * says which job is missing instead.
 */
export function neverScanned(graph: ClusterGraph | null | undefined): boolean {
  return !!graph && (graph.doScanAt ?? null) === null;
}

/** Past this many edges the canvas stops being readable, so the table leads and the
 *  graph goes behind a toggle. */
export const CANVAS_MAX_EDGES = 40;
